import { Fragment, useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Switch } from '@/components/Switch';
import { playChime, readSound, writeSound } from '@/lib/sound';
import { UNDO_SECONDS, readUndoSend, writeUndoSend } from '@/lib/undoSend';
import { useAuth } from '@/lib/auth';
import { useOverlay } from '@/components/Overlay';
import { whoAmI } from '@/lib/training';
import { useChats } from '@/data/store';
import { PhoneSettingsHelp } from '@/components/PhoneSettingsHelp';
import { AlertCheck } from '@/components/AlertCheck';
import { HomeScreenSteps } from '@/components/HomeScreenSteps';
import { FindSuburb } from '@/components/FindSuburb';
import { useDisplay } from '@/lib/display';
import { Avatar } from '@/components/Avatar';
import { BellIcon, ClockIcon, HelpIcon, NewChatIcon } from '@/components/Icons';
import { isAndroid, isIOS, isPhone, phoneFacts, showTestNotification, useDevice, type PhoneFacts } from '@/lib/device';
import { supabase } from '@/lib/supabase';
import { ReportButton, ReportRow } from '@/components/ReportButton';
import { openInSentinel } from '@/lib/contact';
import { AskButton } from '@/components/AskButton';
import { DutySwitch } from '@/components/DutySwitch';
import { setLeoOnDuty, useLeoOnDuty } from '@/lib/duty';

// This phone (home screen, notifications, test), availability, account and sign out.
// On a computer (Gary, 15 Oct) it splits as WhatsApp's desktop settings: SettingsMenu on the
// left (profile and sections), one section in the middle (?s=), Help as its own section.

export type SettingsSection = 'notifications' | 'chats' | 'availability';

const SECTIONS: { key: SettingsSection; label: string; icon: JSX.Element }[] = [
  { key: 'notifications', label: 'Notifications', icon: <BellIcon /> },
  { key: 'chats', label: 'Chats', icon: <NewChatIcon /> },
  { key: 'availability', label: 'My availability', icon: <ClockIcon /> },
];

/** The section open on a computer, from the address; Notifications by default. */
export function useSettingsSection(): SettingsSection {
  const s = useSearchParams()[0].get('s');
  return SECTIONS.some((x) => x.key === s) ? (s as SettingsSection) : 'notifications';
}

/** Left column on a computer: who you are, then the sections, Help and Sign out. */
export function SettingsMenu() {
  const { session, profile, access, signOut } = useAuth();
  const trainer = useTrainer(access.role === 'head_office');
  const navigate = useNavigate();
  const section = useSettingsSection();
  const onHelp = useSearchParams()[0].get('s') === null && window.location.pathname === '/help';
  const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || session?.user.email || '';
  return (
    <>
      {/* The same large title as Chats, Ask and Team (mobile review, 18 Oct). */}
      <header className="chats-listhead">
        <div className="chats-listhead-row">
          <h1 className="chats-listhead-title">Settings</h1>
          <DutySwitch />
          <ReportButton />
          <AskButton />
        </div>
      </header>
      <main className="chats-screen">
        <div className="chats-me-card">
          <Avatar name={name} colour={name.length} />
          <div>
            <b>{name}</b>
            <span>{access.role === 'head_office' ? 'Head office' : 'Operator'}</span>
            <small>{session?.user.email}</small>
          </div>
        </div>
        <nav className="chats-me-menu" aria-label="Settings">
          {SECTIONS.map((x) => (
            <button key={x.key} type="button" className={`chats-me-item${!onHelp && section === x.key ? ' active' : ''}`} onClick={() => navigate(`/settings?s=${x.key}`)}>
              {x.icon}<span>{x.label}</span>
            </button>
          ))}
          <NavLink to="/help" className={({ isActive }) => `chats-me-item${isActive ? ' active' : ''}`}><HelpIcon /><span>Help</span></NavLink>
          {/* Training: head office and the named Dr C reviewers (17 Oct). */}
          {trainer && <NavLink to="/training" className={({ isActive }) => `chats-me-item${isActive ? ' active' : ''}`}><span className="chats-me-noicon" /><span>Training</span></NavLink>}
          <button type="button" className="chats-me-item chats-me-item--danger" onClick={() => void signOut()}><span className="chats-me-noicon" />Sign out</button>
        </nav>
      </main>
    </>
  );
}

/** messaging-send, for the person rather than a chat: a test alert from Sentinel. */
async function personCall<T>(body: Record<string, unknown>): Promise<T | null> {
  const { data, error } = await supabase.functions.invoke('messaging-send', { body });
  return error ? null : data as T;
}

/** The whole page on a phone; one section in the middle on a computer. */
export function SettingsScreen({ section }: { section?: SettingsSection } = {}) {
  const show = (s: SettingsSection | 'phone' | 'account') => (!section ? true : s === section);
  const { session, profile, access, signOut } = useAuth();
  const trainer = useTrainer(access.role === 'head_office');
  const { toast, openSheet } = useOverlay();
  const navigate = useNavigate();
  const { keepArchived, setKeepArchived } = useChats();
  const { density, setDensity, theme, setTheme } = useDisplay();
  const live = !!session && session.user.id !== 'preview';
  // Leo on duty or Available, the same switch as in the header (Gary, 19 Oct).
  const onDuty = useLeoOnDuty(live);
  const [sound, setSound] = useState(readSound);
  const [undoSend, setUndoSend] = useState(readUndoSend);
  const { standalone, notify, askNotifications, canInstall, install } = useDevice();
  // Wording for a computer as well as a phone (Gary, 15 Oct: alerts on a computer).
  const device = isPhone ? 'phone' : 'computer';
  const [facts, setFacts] = useState<PhoneFacts | null>(null);
  const [lastTest, setLastTest] = useState<string>('Not run yet');
  // The test, then "Did you see it?" (17 Oct): a new key asks again after each test.
  const [testRound, setTestRound] = useState(0);
  // No alert went anywhere: the card goes straight to the fix, never "Did you see it?" (mobile review, 19 Oct).
  const [testFailed, setTestFailed] = useState(false);
  // One test at a time, as set-up's button (mobile review, 19 Oct: each tap sent another real alert).
  const [sending, setSending] = useState(false);
  async function sendTest() {
    if (sending) return;
    setSending(true);
    try {
      const at = new Date().toLocaleTimeString('en-GB');
      // The real route first: Sentinel sends it, the same way a new lead arrives.
      const server = live ? await personCall<{ sent: number }>({ action: 'test_push' }) : null;
      if (server && server.sent > 0) {
        setLastTest(`Sent by Sentinel to ${server.sent} device${server.sent === 1 ? '' : 's'} (${at}). Tap it to confirm.`);
        toast(isPhone ? 'Test sent. Lock your phone, then tap the alert when it arrives.' : 'Test sent. Click the alert when it pops up.');
        setTestFailed(false);
      } else {
        const r = await showTestNotification();
        setLastTest(`Sentinel could not reach this ${device}. On this ${device} only: ${r.ok ? 'shown' : 'failed'}, ${r.detail} (${at})`);
        toast(isPhone ? 'Sentinel could not reach this phone. See Phone check below.' : 'Sentinel could not reach this computer. Check notifications are allowed for this site.');
        setTestFailed(!r.ok);
      }
      void phoneFacts().then(setFacts);
      setTestRound((n) => n + 1);
    } finally {
      setSending(false);
    }
  }
  // iPhone in a Safari tab: alerts need the home-screen app first (mobile review, 19 Oct).
  const iphoneTab = isIOS && !standalone;
  const homeSteps = () => openSheet(
    <div className="chats-pad">
      <h2 className="chats-sheet-title">Add to your home screen</h2>
      <HomeScreenSteps canInstall={canInstall} install={() => void install()} />
    </div>,
  );
  useEffect(() => {
    void phoneFacts().then(setFacts);
  }, [notify, standalone]);
  const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || session?.user.email || '';
  const roleLabel = access.role === 'head_office' ? 'Head office' : 'Operator';
  const brandLabel = access.brands.map((b) => (b === 'pos' ? 'Peace of Sleep' : 'CleanMyBed')).join(' and ');

  return (
    <>
      <header className="chats-topbar">
        <h1 className="chats-topbar-title">{section ? SECTIONS.find((x) => x.key === section)?.label : 'Settings'}</h1>
      </header>
      <main className="chats-screen chats-screen--grey">
        {!section && <div className="chats-group">
          <button type="button" className="chats-set" onClick={() => navigate('/help')}>
            <div className="chats-set-main">
              Help
              <small>How CMB Chats works, and how it differs from WhatsApp</small>
            </div>
          </button>
          {/* On a phone these live here, not in the header (Gary, 3 Nov 2026: the header was too busy). */}
          <button type="button" className="chats-set chats-me-phone" onClick={() => void openInSentinel('/')}>
            <div className="chats-set-main">
              Switch to Sentinel
              <small>Open Sentinel, already signed in</small>
            </div>
          </button>
          <ReportRow />
          {trainer && (
            <button type="button" className="chats-set" onClick={() => navigate('/training')}>
              <div className="chats-set-main">
                Training
                <small>Approve messages, train Leo, and give feedback on the app</small>
              </div>
            </button>
          )}
        </div>}
        {show('notifications') && <>
        <p className="chats-group-title">On this {device}</p>
        <div className="chats-group">
          {notify === 'default' ? (
            <button type="button" className="chats-set" onClick={() => void askNotifications()}>
              <div className="chats-set-main">
                Notifications
                <small>Off. Tap to turn on, so new leads ping this {device}.</small>
              </div>
              <span className="chats-set-bad">Turn on</span>
            </button>
          ) : (
            <div className="chats-set">
              <div className="chats-set-main">
                Notifications
                <small>{notify === 'granted'
                  ? isPhone ? 'New leads and replies ping this phone, even when the app is closed.' : 'New messages pop up on this computer while the browser is open, even with this tab in the background.'
                  : notify === 'denied'
                    // In an Android browser tab the site is blocked, not the app: the same advice as the test card (regression run, 19 Oct).
                    ? isAndroid && !standalone ? 'Blocked for this site. Tap the icon left of the address bar, then Permissions (or Notifications), choose Allow, then reload.'
                      : isPhone ? "Blocked. Allow CMB Chats in your phone's Settings." : 'Blocked. Click the padlock beside the web address, set Notifications to Allow, then reload.'
                    : iphoneTab ? 'Off. Add CMB Chats to your Home Screen first.' : 'This browser cannot show notifications.'}</small>
              </div>
              <span className={notify === 'granted' ? 'chats-set-ok' : 'chats-set-bad'}>{notify === 'granted' ? 'On' : 'Off'}</span>
            </div>
          )}
          {isPhone && !standalone ? (
            // Not yet: a tap shows the steps for this browser (mobile review, 19 Oct).
            <button type="button" className="chats-set" onClick={homeSteps}>
              <div className="chats-set-main">
                Added to home screen
                <small>Needed on iPhone for notifications to work.</small>
              </div>
              <span className="chats-set-bad">Not yet</span>
            </button>
          ) : isPhone ? (
            <div className="chats-set">
              <div className="chats-set-main">
                Added to home screen
                <small>Needed on iPhone for notifications to work.</small>
              </div>
              <span className="chats-set-ok">Done</span>
            </div>
          ) : standalone ? (
            <div className="chats-set">
              <div className="chats-set-main">Installed on this computer<small>CMB Chats opens in its own window, like a normal program.</small></div>
              <span className="chats-set-ok">Done</span>
            </div>
          ) : canInstall ? (
            <button type="button" className="chats-set" onClick={() => void install()}>
              <div className="chats-set-main">Install on this computer<small>Its own window and icon in your dock or taskbar, so it is always one click away.</small></div>
              <span className="chats-set-bad">Install</span>
            </button>
          ) : null}
          <button type="button" className="chats-set" disabled={sending} onClick={() => void sendTest()}>
            <div className="chats-set-main">
              {sending ? 'Sending...' : 'Send me a test notification'}
              <small>Sentinel sends a real alert. {isPhone ? 'Tap' : 'Click'} it when it arrives, so head office sees your {device} works.</small>
            </div>
          </button>
        </div>
        {testRound > 0 && <>
          <p className="chats-group-title">Test alert</p>
          <div className="chats-group chats-pad-group">
            <AlertCheck key={testRound} failed={testFailed} onRetest={() => void sendTest()} />
          </div>
        </>}

        </>}
        {/* Phone checks: phones only (Gary, 15 Oct: not on a computer). */}
        {isPhone && show('notifications') && <>
        <p className="chats-group-title">Phone check</p>
        <div className="chats-group">
          <div className="chats-facts">
            <span>Device</span><b>{facts?.device ?? '…'}</b>
            <span>Opened from home screen</span><b>{facts ? (facts.standalone ? 'Yes' : 'No') : '…'}</b>
            <span>Notification permission</span><b>{facts?.permission ?? '…'}</b>
            <span>Background service</span><b>{facts?.serviceWorker ?? '…'}</b>
            <span>Push</span><b>{facts?.push ?? '…'}</b>
            <span>Last test</span><b>{lastTest}</b>
          </div>
        </div>

        {/* The screen as this phone reports it (Gary, 18 Oct: a white strip under the tab bar on iPhone).
            A screenshot of this shows where the app stops and why. */}
        {access.role === 'head_office' && <>
          <p className="chats-group-title">Screen check</p>
          <div className="chats-group">
            <ScreenCheck />
          </div>
        </>}

        <p className="chats-group-title">Notifications not working?</p>
        <div className="chats-group chats-pad-group">
          <PhoneSettingsHelp />
        </div>

        </>}
        {show('chats') && <>
        <p className="chats-group-title">Display</p>
        <div className="chats-group">
          <div className="chats-set chats-set--choice">
            <div className="chats-set-main">
              Chat list
              <small>{density === 'compact' ? 'Name and status only.' : density === 'expanded' ? 'Two lines of the last message.' : 'One line of the last message.'}</small>
            </div>
            <div className="chats-choice" role="radiogroup" aria-label="Chat list">
              {([['compact', 'Compact'], ['normal', 'Normal'], ['expanded', 'Expanded']] as const).map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={density === k} className={`chats-chip${density === k ? ' active' : ''}`} onClick={() => setDensity(k)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="chats-set chats-set--choice">
            <div className="chats-set-main">
              Theme
              <small>{theme === 'system' ? 'Follows your phone or computer.' : 'On this device only.'}</small>
            </div>
            <div className="chats-choice" role="radiogroup" aria-label="Theme">
              {([['light', 'Light'], ['dark', 'Dark'], ['system', 'Match device']] as const).map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={theme === k} className={`chats-chip${theme === k ? ' active' : ''}`} onClick={() => setTheme(k)}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        <p className="chats-group-title">Chats</p>
        <div className="chats-group">
          <button type="button" className="chats-set" onClick={() => setKeepArchived(!keepArchived)}>
            <div className="chats-set-main">
              Keep chats archived
              <small>{keepArchived ? 'Archived chats stay archived when a new message arrives.' : 'Off: an archived chat comes back to your list when the customer writes again, so no lead is missed.'}</small>
            </div>
            <Switch on={keepArchived} label="Keep chats archived" />
          </button>
        </div>

        <p className="chats-group-title">Find a suburb</p>
        <FindSuburb live={live} />

        </>}
        {show('availability') && <>
        <p className="chats-group-title">My availability</p>
        <div className="chats-group">
          <button
            type="button"
            className="chats-set"
            disabled={onDuty === null}
            onClick={() => void setLeoOnDuty(!onDuty).then((ok) => {
              if (!ok) { toast('That did not save. Try again.'); return; }
              toast(onDuty ? 'Available: you answer your chats within 10 minutes' : 'Leo on duty: Leo answers your chats');
            })}
          >
            <div className="chats-set-main">
              Leo on duty
              <small>On: Leo answers your chats and your phone stays quiet; you still get your leads. Off (Available): you answer within 10 minutes, or Leo takes the chat at 20.</small>
            </div>
            <Switch on={onDuty === true} label="Leo on duty" />
          </button>
          <div className="chats-set">
            <div className="chats-set-main">
              Working hours
              <small>Mon to Fri, 08:00 to 17:00, the same for everyone for now. Outside these hours, and on public holidays, Leo is on duty for everyone.</small>
            </div>
          </div>
          <button type="button" className="chats-set" onClick={() => { const next = !sound; setSound(next); writeSound(next); if (next) playChime(); }}>
            <div className="chats-set-main">
              Message sound
              <small>A chime when a customer writes while the app is open. Alerts with the app closed use your phone's sound.</small>
            </div>
            <Switch on={sound} label="Message sound" />
          </button>
          <button type="button" className="chats-set" onClick={() => { const next = !undoSend; setUndoSend(next); writeUndoSend(next); }}>
            <div className="chats-set-main">
              Undo before sending
              <small>{`A reply to a customer waits ${UNDO_SECONDS} seconds with an Undo button before it goes, as WhatsApp cannot unsend. Switch off to send at once.`}</small>
            </div>
            <Switch on={undoSend} label="Undo before sending" />
          </button>
        </div>

        </>}
        {show('account') && <>
        <p className="chats-group-title">Account</p>
        <div className="chats-group">
          <div className="chats-set">
            <div className="chats-set-main">
              {name}
              <small>{session?.user.email}</small>
            </div>
          </div>
          <div className="chats-set">
            <div className="chats-set-main">
              {roleLabel}
              <small>Chats for {brandLabel}. Same login as Sentinel.</small>
            </div>
          </div>
          <button className="chats-set chats-set--danger" type="button" onClick={() => void signOut()}>
            <div className="chats-set-main">Sign out</div>
          </button>
        </div>
        </>}
        <p className="chats-foot">
          Customer messages come from and go to their WhatsApp, on the CleanMyBed business number.
          {live ? ' What you send here reaches the customer.' : ' Sample chats: nothing here reaches a real customer.'}
        </p>
      </main>
    </>
  );
}

/** Head office, or a named Dr C reviewer (asked once): they see Training in Settings. */
function useTrainer(headOffice: boolean): boolean {
  const [reviewer, setReviewer] = useState(false);
  useEffect(() => {
    if (headOffice) return;
    let gone = false;
    void whoAmI().then((w) => { if (!gone) setReviewer(w.reviewer); });
    return () => { gone = true; };
  }, [headOffice]);
  return headOffice || reviewer;
}

/** Screen sizes in points, as the phone reports them: the layout, the visible part, the notch and the app. */
function ScreenCheck() {
  const [, tick] = useState(0);
  useEffect(() => {
    const again = () => tick((n) => n + 1);
    window.addEventListener('resize', again);
    window.visualViewport?.addEventListener('resize', again);
    return () => { window.removeEventListener('resize', again); window.visualViewport?.removeEventListener('resize', again); };
  }, []);
  const probe = document.createElement('div');
  probe.className = 'chats-safe-probe';
  document.body.appendChild(probe);
  const st = getComputedStyle(probe);
  const top = Math.round(parseFloat(st.paddingTop));
  const bottom = Math.round(parseFloat(st.paddingBottom));
  probe.remove();
  const app = document.querySelector('.chats-app')?.getBoundingClientRect();
  const tabs = document.querySelector('.chats-tabs')?.getBoundingClientRect();
  const rows: [string, string][] = [
    ['Phone screen', `${screen.width} × ${screen.height}`],
    ['Page height', `${window.innerHeight}`],
    ['Visible height', `${Math.round(window.visualViewport?.height ?? 0)}`],
    ['Notch top / bottom', `${top} / ${bottom}`],
    ['App top / bottom', app ? `${Math.round(app.top)} / ${Math.round(app.bottom)}` : '?'],
    ['Tab bar bottom', tabs ? `${Math.round(tabs.bottom)}` : 'not on this screen'],
    ['Home screen mode', matchMedia('(display-mode: standalone)').matches ? 'Yes' : 'No'],
    ['Older install adjusted', document.documentElement.classList.contains('chats-short-window') ? 'Yes' : 'No'],
  ];
  return (
    <div className="chats-facts">
      {rows.map(([k, v]) => <Fragment key={k}><span>{k}</span><b>{v}</b></Fragment>)}
    </div>
  );
}
